import { Link } from "react-router-dom";
import { Palette, Layers, Phone, ArrowRight, Shirt, ChevronRight, Package } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import { Button } from "@/components/ui/button";

const options = [
  {
    id: "lining",
    to: "/catalogue/600-lining",
    pill: "Lining Fabric",
    title: "Order Lining by Quality & Shade",
    desc:
      "6 qualities — 600 SD, 060 DD, 006 DD Mercerised, 6000 SD, 303 & 3000 Pure Cotton. 156+ shades across 4 master charts.",
    bg: "var(--mp-copper-solid)",
    icon: Shirt,
    hint: "Pick quality → shade # → meters → WhatsApp",
  },
  {
    id: "crepe",
    to: "/catalogue/american-crepe",
    pill: "American Crepe",
    title: "Order Crepe by Shade Number",
    desc: "19 swatch charts covering 700+ crepe shades. Zoom in, read the shade number, add meters, send to WhatsApp.",
    bg: "var(--mp-solid)",
    icon: Palette,
    hint: "Zoom → note number → type → WhatsApp",
  },
  {
    id: "colours",
    to: "/colours",
    pill: "Colour Families",
    title: "Browse Colours by Family",
    desc:
      "Visual gradient explorer — pastels, earth tones, jewel, neon, metallic. Auto-calc bundles + 5% GST + Hamali charges.",
    bg: "var(--mp-copper-deep-solid)",
    icon: Layers,
    hint: "Browse family → add to cart → instant quote",
  },
];

export default function OrderHub() {
  return (
    <PageLayout>
      <SEO
        title="Order Lining Fabric Online — By Shade, Sample Kit & WhatsApp | Mutha Paras & Co."
        description="Order lining fabric by quality and shade number, get a Rs. 299 sample kit delivered pan-India, or chat with our team directly on WhatsApp."
        path="/order"
      />
      <section className="py-12 lg:py-20" data-testid="order-hub">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
            >
              Place an Order
            </p>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              How would you like to order?
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              Choose the fabric family you want — pick shades, set meters, and send the complete order
              straight to our WhatsApp. We typically reply within an hour.
            </p>
          </div>

          {/* Option cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-16" data-testid="order-options">
            {options.map((opt) => {
              const Icon = opt.icon;
              return (
                <Link
                  key={opt.id}
                  to={opt.to}
                  className="group block"
                  data-testid={`order-option-${opt.id}`}
                >
                  <div
                    className="h-full p-6 sm:p-8 rounded-sm border border-[var(--mp-line)] transition-all duration-300 hover:shadow-lg"
                    style={{ backgroundColor: opt.bg }}
                  >
                    <div
                      className="w-12 h-12 rounded-sm mb-6 flex items-center justify-center"
                      style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <p
                      className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-2"
                      style={{ color: "rgba(255,255,255,0.88)", fontFamily: "'Manrope', sans-serif" }}
                    >
                      {opt.pill}
                    </p>
                    <h3
                      className="text-2xl sm:text-3xl font-medium text-white mb-3 leading-tight"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {opt.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed mb-6"
                      style={{ color: "rgba(255,255,255,0.85)", fontFamily: "'Manrope', sans-serif" }}
                    >
                      {opt.desc}
                    </p>
                    <div className="border-t border-white/20 pt-4 flex items-center justify-between">
                      <span
                        className="text-xs"
                        style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {opt.hint}
                      </span>
                      <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Featured: Sample Kit */}
          <Link
            to="/sample-kit"
            className="block mb-12 group"
            data-testid="order-sample-kit-cta"
          >
            <div
              className="rounded-2xl border border-[var(--mp-line)] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 transition-all duration-300 hover:shadow-lg bg-white"
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "var(--mp-surface-2)" }}
              >
                <Package className="w-6 h-6" style={{ color: "var(--mp-copper)" }} />
              </div>
              <div className="flex-1">
                <p
                  className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1"
                  style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                >
                  New · Pan-India · Flat ₹299
                </p>
                <h3
                  className="text-2xl md:text-3xl font-medium tracking-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                >
                  Order a Sample Kit
                </h3>
                <p
                  className="text-sm mt-1"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                >
                  Feel every fabric before committing to a bulk order. Sealed kit with all 9 qualities delivered to your door.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" style={{ color: "var(--mp-text-strong)" }} />
            </div>
          </Link>

          {/* Direct WhatsApp fallback */}
          <div
            className="rounded-sm border border-[var(--mp-line)] p-6 sm:p-8 pastel-cream text-center"
            data-testid="order-whatsapp-fallback"
          >
            <h3
              className="text-2xl font-medium mb-2"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Prefer to chat directly?
            </h3>
            <p
              className="text-sm mb-5 max-w-xl mx-auto"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              Skip the catalogue and talk to our team directly. Share a photo, describe what you need, or
              just ask a question — we&apos;re on WhatsApp Monday–Saturday, 10am–7pm.
            </p>
            <a
              href="https://wa.me/917624853840"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-direct-wa"
            >
              <Button
                className="rounded-sm text-sm px-6 py-5 text-white"
                style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Chat on WhatsApp · +91 76248 53840
              </Button>
            </a>
          </div>

          {/* Quick links */}
          <div
            className="mt-12 pt-8 border-t border-[var(--mp-line)] flex flex-col sm:flex-row gap-4 justify-center text-center"
            data-testid="order-quick-links"
          >
            <Link
              to="/products"
              className="text-sm hover:underline inline-flex items-center justify-center gap-1"
              style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
            >
              View all 9 fabric qualities <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/exchange-policy"
              className="text-sm hover:underline inline-flex items-center justify-center gap-1"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              Read our exchange policy <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/delivery"
              className="text-sm hover:underline inline-flex items-center justify-center gap-1"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              Delivery & shipping info <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      <FAQAccordion />
    </PageLayout>
  );
}
